import React, { useEffect, useMemo } from 'react';
import { groupOf, normalizeCardPlayer } from '../cards/cardUtils.js';
import { POSITION_FULL_NAMES } from './Constants.js';

const GROUP_ORDER = ['GKP', 'DEF', 'MID', 'FWD'];
const GROUP_LABELS = {
  GKP: 'Goalkeepers',
  DEF: 'Defenders',
  MID: 'Midfielders',
  FWD: 'Forwards',
};

// Where a player currently is, shown as a chip on their row.
function playerStatus(wyId, playersOnField, subs) {
  const fieldSlot = playersOnField.find((slot) => slot.player_id === wyId);
  if (fieldSlot) return { kind: 'field', label: fieldSlot.title };
  const isOnBench = subs.some((sub) => sub.playerInfo.wyId === wyId);
  if (isOnBench) return { kind: 'bench', label: 'SUB' };
  return null;
}

/**
 * Bottom sheet opened by tapping a pitch or bench slot on mobile. Shows the
 * slot's position, the current occupant (with a remove action), and the whole
 * squad grouped by role so any player can be placed with one tap.
 *
 * slot: { type: 'field', slotId, position } | { type: 'bench', slotNumber, position }
 */
function SlotPickerDialog({
  slot,
  currentPlayer,
  players,
  playersOnField,
  subs,
  onPick,
  onRemove,
  onClose,
}) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // The page behind the sheet must not scroll while picking.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const isFieldSlot = slot.type === 'field';
  const positionCode = slot.position || null;
  const positionName = positionCode ? POSITION_FULL_NAMES[positionCode] : null;

  const groupedRows = useMemo(() => {
    const rows = (players || [])
      .filter((player) => player.wyId !== currentPlayer?.wyId)
      .map((player) => {
        const card = normalizeCardPlayer(player);
        return {
          player,
          name: card.name || player.name,
          positionLabel: card.positionLabel || '-',
          group: card.positionGroup || 'MID',
          overall: card.overall,
          status: playerStatus(player.wyId, playersOnField, subs),
        };
      });

    // For a pitch slot, lead with the group that fits the position.
    const slotGroup = isFieldSlot ? groupOf(positionCode) : null;
    const order = slotGroup
      ? [slotGroup, ...GROUP_ORDER.filter((group) => group !== slotGroup)]
      : GROUP_ORDER;

    return order
      .map((group) => ({
        group,
        label: GROUP_LABELS[group],
        rows: rows
          .filter((row) => row.group === group)
          .sort((a, b) => (b.overall ?? -1) - (a.overall ?? -1)),
      }))
      .filter((section) => section.rows.length > 0);
  }, [players, currentPlayer, playersOnField, subs, isFieldSlot, positionCode]);

  const heading = isFieldSlot
    ? (positionCode || 'Position')
    : `Substitute ${slot.slotNumber}`;
  const subheading = isFieldSlot
    ? [positionName, 'Starting XI'].filter(Boolean).join(', ')
    : (positionCode
      ? `Comes on as ${POSITION_FULL_NAMES[positionCode] || positionCode}`
      : 'Bench slot');

  return (
    <div
      className="slot-picker"
      role="dialog"
      aria-modal="true"
      aria-label={`Pick a player for ${heading}`}
      onClick={onClose}
    >
      <div className="slot-picker__sheet" onClick={(event) => event.stopPropagation()}>
        <div className="slot-picker__head">
          <div className="slot-picker__slot">
            <span className="slot-picker__code">{heading}</span>
            <span className="slot-picker__slot-name">{subheading}</span>
          </div>
          <button
            type="button"
            className="slot-picker__close icon-s-cross"
            aria-label="Close player picker"
            onClick={onClose}
          />
        </div>

        {currentPlayer ? (
          <div className="slot-picker__current">
            <div className="slot-picker__current-info">
              <span className="slot-picker__current-label">In this slot</span>
              <span className="slot-picker__current-name">{currentPlayer.name}</span>
            </div>
            <button
              type="button"
              className="slot-picker__remove"
              onClick={onRemove}
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="slot-picker__current slot-picker__current--empty">
            <span className="slot-picker__current-label">Empty slot</span>
            <span className="slot-picker__current-hint">Tap a player below to place them here</span>
          </div>
        )}

        <div className="slot-picker__list">
          {groupedRows.map((section) => (
            <div key={section.group} className="slot-picker__group">
              <div className="slot-picker__group-title">{section.label}</div>
              {section.rows.map((row) => (
                <button
                  key={row.player.wyId}
                  type="button"
                  className="slot-picker__row"
                  onClick={() => onPick(row.player)}
                >
                  <span className="slot-picker__ovr">{row.overall ?? '-'}</span>
                  <span className="slot-picker__player">
                    <span className="slot-picker__player-name">{row.name}</span>
                    <span className="slot-picker__player-pos">{row.positionLabel}</span>
                  </span>
                  {row.status ? (
                    <span className={`slot-picker__status slot-picker__status--${row.status.kind}`}>
                      {row.status.label}
                    </span>
                  ) : (
                    <span className="slot-picker__status slot-picker__status--free">Free</span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SlotPickerDialog;
